import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { VendorState } from '../../types/vendors';
import { vendorApi } from './api';
import { VendorCreate, VendorUpdate } from './types';

// Async thunks
export const fetchVendors = createAsyncThunk(
  'vendors/fetchVendors',
  async (_, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      return await vendorApi.fetchAll(user.id);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addVendor = createAsyncThunk(
  'vendors/addVendor',
  async (vendor: VendorCreate, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      return await vendorApi.create(vendor, user.id);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateVendor = createAsyncThunk(
  'vendors/updateVendor',
  async (vendor: VendorUpdate, thunkAPI) => {
    try {
      return await vendorApi.update(vendor);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteVendor = createAsyncThunk(
  'vendors/deleteVendor',
  async (id: string, thunkAPI) => {
    try {
      // First check if vendor has any associated sales
      const { data: sales, error: salesError } = await supabase
        .from('sales')
        .select('id, sale_date, total_amount')
        .eq('vendor_id', id);

      if (salesError) throw salesError;

      if (sales && sales.length > 0) {
        const salesInfo = sales.map(s => ({
          id: s.id,
          date: new Date(s.sale_date).toLocaleDateString(),
          amount: s.total_amount
        }));
        throw new Error(JSON.stringify({
          message: `Cannot delete vendor - found ${sales.length} associated sales. Please delete these sales first:`,
          sales: salesInfo
        }));
      }

      // If no sales found, proceed with deletion
      const { error } = await supabase
        .from('vendors')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message.startsWith('{') ? error.message : 'Failed to delete vendor'
      );
    }
  }
);

const initialState: VendorState = {
  vendors: [],
  loading: false,
  error: null,
};

const vendorSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload;
        state.error = null;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addVendor.fulfilled, (state, action) => {
        state.vendors.push(action.payload);
      })
      .addCase(updateVendor.fulfilled, (state, action) => {
        const index = state.vendors.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.vendors[index] = action.payload;
        }
      })
      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.vendors = state.vendors.filter(v => v.id !== action.payload);
      });
  },
});

export const { clearError } = vendorSlice.actions;
export default vendorSlice.reducer;